<template>
  <!-- prettier-ignore -->
  <div class="prompt">
    <ovp-typography
      class="prompt__text"
      tag="span"
      type="body04"
      v-text="$t('login.prompt.text')"
    />

    <a
      class="prompt__title"
      @click="openLoginModal"
      v-text="$t('login.prompt.title')"
    />

    <ovp-modal
      v-model="isLoginModalOpen"
      x-only
      @close="isLoginModalOpen = false"
    >
      <div class="prompt__modal">
        <component
          :is="current.component"
          v-bind="current.props"
          @back="current.back"
          @error="current.error"
          @update="current.proceed"
        />
      </div>
    </ovp-modal>
  </div>
</template>

<script>
import OvpModal from '@/components/Modal/Modal'
import { BasketHelper, CollectiveHelper } from '@/helpers'
import { ContractHelper } from '@/helpers/Contract'
import { LOGIN_ERROR_STATE } from '@/config/constants'
import BasketMixin from '@/mixins/Basket'
import { AuthHelper } from '@/components/Login/steps/kupo/Auth'

const AUTH_HN_FORM = 'auth-hn-form'
const AUTH_KUPO_FORM = 'auth-kupo-form'
const AUTH_PERSONS = 'auth-persons-form'
const ERROR_GENERAL = 'error-general'
const ERROR_LOCKED = 'error-locked'
const ERROR_LOGGED_OUT = 'error-logged-out'
const SUCCESS = 'success'
const TYPE_SELECTION = 'type-selection'

export default {
  name: 'OvpLoginPrompt',

  mixins: [BasketMixin],

  components: { OvpModal },

  props: {
    hideNotification: {
      type: Boolean,
      default: false,
    },

    submitCallback: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      current: {},
      global: {
        basket: undefined,
        email: undefined,
        loginType: undefined,
        mobile: undefined,
        partnerNumber: undefined,
        selectedPersons: [],
        uid: undefined,
      },
      isLoginModalOpen: false,
    }
  },

  computed: {
    headOfContract() {
      const contractOwnerId = this.global.basket.contractOwner.personId
      return this.global.basket.persons.find(p => p.personId === contractOwnerId)
    },

    steps() {
      return [
        {
          id: TYPE_SELECTION,
          component: () =>
            import(/* webpackChunkName: "login-type-selection" */ '@/components/Login/steps/TypeSelection'),
          back: () => {},
          error: state => {
            this.handleError(state)
          },
          proceed: key => {
            this.current = this.findById(`auth-${key}-form`)
            this.global.loginType = key
          },
          props: {},
        },
        {
          id: AUTH_KUPO_FORM,
          component: () => import(/* webpackChunkName: "login-auth-kupo" */ '@/components/Login/steps/kupo/Form'),
          back: () => {
            this.current = this.findById(TYPE_SELECTION)
          },
          error: state => {
            this.handleError(state)
          },
          proceed: () => {
            this.current = this.findById(SUCCESS)
          },
          props: {},
        },
        {
          id: AUTH_HN_FORM,
          component: () => import(/* webpackChunkName: "login-auth-hn" */ '@/components/Login/steps/hn/Form'),
          back: () => {
            this.current = this.findById(TYPE_SELECTION)
          },
          error: state => {
            this.handleError(state)
          },
          proceed: ({ partnerNumber, uid }) => {
            this.global.partnerNumber = partnerNumber
            this.global.uid = uid
            this.current = this.findById(SUCCESS)
          },
          props: {},
        },
        {
          id: SUCCESS,
          component: () => import(/* webpackChunkName: "login-success" */ '@/components/Login/steps/success/Form'),
          back: () => {},
          error: state => {
            this.handleError(state)
          },
          proceed: ({ basket, mobile, email }) => {
            this.global.basket = basket
            this.global.mobile = mobile || undefined
            this.global.email = email || undefined
            this.current = this.findById(AUTH_PERSONS)
          },
          props: {
            isKupoLogin: this.global.loginType === 'kupo',
            partnerNumber: this.global.partnerNumber,
            uid: this.global.uid,
          },
        },

        {
          id: AUTH_PERSONS,
          component: () => import(/* webpackChunkName: "login-auth-persons" */ '@/components/Login/steps/persons/Form'),
          back: () => {},
          error: state => {
            this.handleError(state)
          },
          proceed: persons => {
            // Create basket
            this.global.selectedPersons = persons
            this.createBasket()
          },
          props: {
            basketPersons: this.global.basket?.persons || [],
          },
        },
        {
          id: ERROR_GENERAL,
          component: () =>
            import(/* webpackChunkName: "login-auth-error-general" */ '@/components/Login/error/General'),
          back: () => {
            this.isLoginModalOpen = false
          },
          error: () => {},
          proceed: () => {},
        },
        {
          id: ERROR_LOCKED,
          component: () => import(/* webpackChunkName: "login-auth-error-locked" */ '@/components/Login/error/Locked'),
          back: () => {
            this.isLoginModalOpen = false
          },
          error: () => {},
          proceed: () => {},
        },
        {
          id: ERROR_LOGGED_OUT,
          component: () =>
            import(/* webpackChunkName: "login-auth-error-logged-out" */ '@/components/Login/error/LoggedOut'),
          back: () => {
            this.isLoginModalOpen = false
          },
          error: () => {},
          proceed: () => {},
        },
      ]
    },
  },

  methods: {
    async createBackendBasket(basket) {
      if (this.global.loginType === 'kupo') {
        return await AuthHelper.createBasket({ payload: basket })
      } else {
        return await this.$backend.ovp.createBasketForPartnerNumber({
          payload: basket,
          partnerNumber: this.global.partnerNumber,
          uid: this.global.uid,
        })
      }
    },

    createBasket() {
      const basket = this.prepareBasket()
      this.$loading.start()
      this.createBackendBasket(basket)
        .then(basket => BasketHelper.updateBasketSession(basket))
        .then(basket => CollectiveHelper.loadCollective(basket))
        .then(basket => BasketHelper.getAndSetProducts(basket))
        .then(basket => BasketHelper.getAndSetDistributionPartnerByBasket(basket))
        .then(basket => ContractHelper.loadContract(basket))
        .then(() => {
          this.isLoginModalOpen = false
          this.submitCallback()
          if (!this.hideNotification) {
            this.$notification.add(
              this.$t('login.prompt.message', {
                firstName: this.headOfContract.personData.firstName,
                lastName: this.headOfContract.personData.lastName,
              }),
              'warning'
            )
          }
        })
        .catch(() => {
          this.current = this.findById(ERROR_GENERAL)
        })
        .finally(() => {
          this.$loading.end()
        })
    },

    handleError(state) {
      switch (state) {
        case LOGIN_ERROR_STATE.LOCKED:
          this.current = this.findById(ERROR_LOCKED)
          break
        case LOGIN_ERROR_STATE.LOGGED_OUT:
          this.current = this.findById(ERROR_LOGGED_OUT)
          break
        case LOGIN_ERROR_STATE.GENERAL:
        default:
          this.current = this.findById(ERROR_GENERAL)
      }
    },

    openLoginModal() {
      // reset/initialise state on opening the initial modal
      this.reset()

      // open the modal
      this.isLoginModalOpen = true
    },

    prepareBasket() {
      const { basket, email, mobile, selectedPersons } = this.global
      const newBasket = { ...basket, persons: selectedPersons }
      if (email) newBasket.email = email
      if (mobile) newBasket.mobile = mobile
      // Overwrite lang with currently selected language
      newBasket.language = this.$i18n.locale.toUpperCase()
      return newBasket
    },

    findById(id) {
      return this.steps.find(s => s.id === id)
    },

    reset() {
      const defaultStep = TYPE_SELECTION

      // reset state back to the initial-screen (type selection)
      this.current = this.findById(defaultStep)
    },
  },
}
</script>

<style name="mobile" scoped>
.prompt {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__modal {
    padding: 40px 40px 0;
  }
}

.prompt__title {
  color: var(--c-original-green);
}

.prompt__title:hover {
  color: var(--c-sanitas-green);
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .prompt {
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }
}
</style>
